/**
 * Application route paths, in one place.
 *
 * Routes are declared in routes/Routes.tsx, but every navigation in the app
 * hand-builds its target as a template literal. Nothing connects the two, so a
 * path can be written that no route matches and the mistake only surfaces as a
 * 404 when a user clicks it. That is exactly how the dashboard's contract-end
 * card came to link at /vehicule/{id} while the route is /vehicule/update/:id.
 *
 * These builders are the single source of truth for anything that navigates.
 * Prefer them over string literals; the compiler cannot catch a wrong literal,
 * but it will catch a missing or misspelled builder.
 *
 * Keep in sync with routes/Routes.tsx and seo.config.ts.
 */

type Id = string | number;

export const paths = {
  dashboard: "/dashboard",
  aiAgent: "/ai-agent",
  aide: "/aide",
  billing: "/billing",
  cardsAndBadges: "/carteAndBadge",
  geolocation: "/geolocalisation",
  fiscalite: "/fiscalite",
  analyses: "/analyses",
  tco: "/tco",
  parametre: "/parametre",

  /**
   * Documents contractuels, affiches comme des pages de l'application.
   *
   * Doit rester aligne sur LEGAL_DOCUMENT_PATHS dans src/legal/documents.ts :
   * c'est ce module-la que le formulaire d'inscription interroge, celui-ci sert
   * a toute autre navigation (pied de page, ecran Parametres).
   */
  legal: {
    terms: "/legal/terms",
    dpa: "/legal/dpa",
  },

  documents: {
    list: "/documents/list",
  },

  vehicle: {
    list: "/vehicule/list",
    create: "/vehicule/create",
    /** Vehicle detail. NOTE the `update` segment - /vehicule/:id is NOT a route. */
    update: (id: Id) => `/vehicule/update/${id}`,
    createEvent: (id: Id) => `/vehicule/update/${id}/create-event`,
    event: (id: Id, eventId: Id) => `/vehicule/update/${id}/event/${eventId}`,
    /** History is the one vehicle route with no `update` segment. */
    history: (id: Id) => `/vehicule/${id}/history`,
    batteryHealth: (id: Id) => `/vehicule/battery-health/${id}`,
  },

  collaborator: {
    list: "/collaborator/list",
    create: "/collaborator/create",
    update: (id: Id) => `/collaborator/update/${id}`,
  },

  claims: {
    list: "/claims/list",
    create: "/claims/create",
    update: (id: Id) => `/claims/update/${id}`,
  },

  notifications: {
    list: "/notification/list",
  },
} as const;

export default paths;
